import React, { useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
} from '@mui/material';
import { colors } from '../theme';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EvStationIcon from '@mui/icons-material/EvStation';
import BoltIcon from '@mui/icons-material/Bolt';

function ProjectsSection() {
  const scrollContainerRef = useRef(null);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const containerWidth = container.offsetWidth;
      const scrollAmount = containerWidth * 0.85; // Scroll almost one card width
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const projects = [
    {
      title: 'Boathouse Residences',
      image: '/sitephotos/boathouse1.jpg',
      chargers: '5 AC Chargers',
      power: '22kw',
      year: '2022',
      tags: ['AC', 'Residential'],
    },
    {
      title: 'Capella Singapore',
      image: '/sitephotos/capella1.jpg',
      chargers: '3 AC Chargers',
      power: '22kW each',
      year: '2023',
      tags: ['AC', 'Hotel', 'Public'],
    },
    {
      title: 'Corals at Keppel Bay',
      image: '/sitephotos/corals1.jpg',
      chargers: '4 AC Chargers',
      power: '7.4kw',
      year: '2022',
      tags: ['AC', 'Residential'],
    },
    {
      title: 'Enterprise One',
      image: '/sitephotos/e1.jpg',
      chargers: '1 DC Chargers',
      power: '200kw',
      year: '2024',
      tags: ['DC', 'Commercial', 'Public'],
    },
    {
      title: 'Hedges Park Condominium',
      image: '/sitephotos/hedgespark1.jpg',
      chargers: '2 AC Chargers',
      power: '22kW each',
      year: '2022',
      tags: ['AC', 'Residential'],
    },
    {
      title: 'Hillvista Condominium',
      image: '/sitephotos/hillvista1.jpg',
      chargers: '2 AC Chargers',
      power: '22kw',
      year: '2023',
      tags: ['AC', 'Residential'],
    },
    {
      title: 'North View Bizhub',
      image: '/sitephotos/northview1.jpg',
      chargers: '4 AC Chargers',
      power: '22kw',
      year: '2024',
      tags: ['AC', 'Industrial', 'Public'],
    },
    {
      title: '8 @ Mount Sophia',
      image: '/sitephotos/mount1.jpg',
      chargers: '12 AC Chargers',
      power: '22kw & 4.6kw',
      year: '2024',
      tags: ['AC', 'Residential'],
    },
  ];

  return (
    <Box
      id="projects"
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: '#f8f8f8',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 800,
              mb: 2,
              color: colors.black,
            }}
          >
            Completed{' '}
            <Box component="span" sx={{ color: colors.green }}>
              Projects
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              maxWidth: '700px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.2rem' },
              mb: 2,
            }}
          >
            Here are some of our completed projects across Singapore
          </Typography>
          <Typography
            sx={{
              color: '#888',
              maxWidth: '650px',
              mx: 'auto',
              fontSize: { xs: '0.95rem', md: '1rem' },
            }}
          >
            From residential condominiums to commercial properties, we deliver reliable EV charging infrastructure
          </Typography>
        </Box>

        {/* Carousel Container */}
        <Box sx={{ position: 'relative', mx: { xs: 0, sm: 2, md: 6 } }}>
          {/* Left Arrow */}
          <IconButton
            onClick={() => scroll('left')}
            sx={{
              position: 'absolute',
              left: { xs: -8, sm: -16, md: -50 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: colors.white,
              boxShadow: 3,
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              '&:hover': {
                bgcolor: colors.green,
                color: colors.white,
              },
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
          </IconButton>

          {/* Right Arrow */}
          <IconButton
            onClick={() => scroll('right')}
            sx={{
              position: 'absolute',
              right: { xs: -8, sm: -16, md: -50 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: colors.white,
              boxShadow: 3,
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              '&:hover': {
                bgcolor: colors.green,
                color: colors.white,
              },
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
          </IconButton>

          {/* Scrollable Projects */}
          <Box
            ref={scrollContainerRef}
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: { xs: 2, sm: 3, md: 4 },
              py: 2,
              px: { xs: 0.5, sm: 1 },
              scrollBehavior: 'smooth',
              scrollSnapType: 'x mandatory',
              '&::-webkit-scrollbar': {
                height: { xs: 4, sm: 8 },
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: '#f0f0f0',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: colors.green,
                borderRadius: 4,
                '&:hover': {
                  bgcolor: colors.yellow,
                },
              },
            }}
          >
            {projects.map((project, index) => (
              <Card
                key={index}
                sx={{
                  minWidth: { 
                    xs: '85%', 
                    sm: 'calc((100% - 24px) / 2)', 
                    md: 'calc((100% - 64px) / 3)' 
                  },
                  maxWidth: { 
                    xs: '85%', 
                    sm: 'calc((100% - 24px) / 2)', 
                    md: 'calc((100% - 64px) / 3)' 
                  },
                  flexShrink: 0,
                  borderRadius: 4,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  scrollSnapAlign: 'start',
                  border: project.highlight
                    ? `3px solid ${colors.green}`
                    : '1px solid #e0e0e0',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 16px 48px ${colors.green}30`,
                  },
                }}
              >
                {/* Project Image */}
                <CardMedia
                  sx={{
                    height: 220,
                    bgcolor: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <Box
                    component="img"
                    src={project.image}
                    alt={project.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const parent = e.target.parentElement;
                      if (parent && !parent.querySelector('.placeholder-content')) {
                        parent.innerHTML += `
                          <div class="placeholder-content" style="display: flex; flex-direction: column; align-items: center; justify-content: center; width: 100%; height: 100%; position: absolute; top: 0; left: 0;">
                            <svg width="80" height="80" viewBox="0 0 24 24" fill="${colors.green}">
                              <path d="M12,2A2,2 0 0,1 14,4C14,4.74 13.6,5.39 13,5.73V7H14A7,7 0 0,1 21,14H22A1,1 0 0,1 23,15V18A1,1 0 0,1 22,19H21V20A2,2 0 0,1 19,22H5A2,2 0 0,1 3,20V19H2A1,1 0 0,1 1,18V15A1,1 0 0,1 2,14H3A7,7 0 0,1 10,7H11V5.73C10.4,5.39 10,4.74 10,4A2,2 0 0,1 12,2M8.5,14A1.5,1.5 0 0,0 7,15.5A1.5,1.5 0 0,0 8.5,17A1.5,1.5 0 0,0 10,15.5A1.5,1.5 0 0,0 8.5,14M15.5,14A1.5,1.5 0 0,0 14,15.5A1.5,1.5 0 0,0 15.5,17A1.5,1.5 0 0,0 17,15.5A1.5,1.5 0 0,0 15.5,14Z"/>
                            </svg>
                            <p style="margin-top: 12px; color: #999; font-weight: 500; font-size: 12px;">Project Image</p>
                          </div>
                        `;
                      }
                    }}
                  />
                  
                  {/* Year Badge */}
                  <Chip
                    label={project.year}
                    sx={{
                      position: 'absolute',
                      top: { xs: 12, sm: 16 },
                      right: { xs: 12, sm: 16 },
                      bgcolor: colors.green,
                      color: colors.white,
                      fontWeight: 700,
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    }}
                  />
                </CardMedia>

                <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                  {/* Project Title */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: colors.black,
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                    }}
                  >
                    {project.title}
                  </Typography>

                  {/* Stats */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: { xs: 1.5, sm: 2 },
                      mb: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EvStationIcon sx={{ color: colors.green, fontSize: { xs: 16, sm: 18 } }} />
                      <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, color: '#666' }}>
                        {project.chargers}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <BoltIcon sx={{ color: colors.yellow, fontSize: { xs: 16, sm: 18 } }} />
                      <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, color: '#666' }}>
                        {project.power}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Tags */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {project.tags.map((tag, idx) => (
                      <Chip
                        key={idx}
                        label={tag}
                        size="small"
                        sx={{
                          bgcolor: `${colors.green}15`,
                          color: colors.green,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default ProjectsSection;
